<template>
  <div class="counter-container">
    <div class="d-flex counter-stick__container space-between">
      <div class="counter-stick"></div>
      <div class="counter-stick"></div>
    </div>
    <div class="counter-border bg__counterBackground">
      <div class="counter-content">
        <div class="time-section">
          <div class="time-unit">
            <div class="time-value">
              <div class="digit col__counterText bg__white">
                {{ daysDigits[0] }}
              </div>
              <div class="digit col__counterText bg__white">
                {{ daysDigits[1] }}
              </div>
            </div>
            <div class="time-label col__white">
              <slot name="days">{{ $t("counter.days") }}</slot>
            </div>
          </div>
          <div class="time-separator col__white">:</div>
          <div class="time-unit">
            <div class="time-value">
              <div class="digit col__counterText bg__white">
                {{ hoursDigits[0] }}
              </div>
              <div class="digit col__counterText bg__white">
                {{ hoursDigits[1] }}
              </div>
            </div>
            <div class="time-label col__white">
              <slot name="hours">{{ $t("counter.hours") }}</slot>
            </div>
          </div>
          <div class="time-separator col__white">:</div>
          <div class="time-unit">
            <div class="time-value">
              <div class="digit col__counterText bg__white">
                {{ minutesDigits[0] }}
              </div>
              <div class="digit col__counterText bg__white">
                {{ minutesDigits[1] }}
              </div>
            </div>
            <div class="time-label col__white">
              <slot name="minutes">{{ $t("counter.minutes") }}</slot>
            </div>
          </div>
          <div class="time-separator col__white">:</div>
          <div class="time-unit">
            <div class="time-value">
              <div class="digit col__counterText bg__white">
                {{ secondsDigits[0] }}
              </div>
              <div class="digit col__counterText bg__white">
                {{ secondsDigits[1] }}
              </div>
            </div>
            <div class="time-label col__white">
              <slot name="seconds">{{ $t("counter.seconds") }}</slot>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { zeroPad } from "~/utils";

export default {
  name: "Counter",
  props: {
    /**
     * Target end time for countdown
     */
    endTime: {
      type: [String, Number, Date],
      required: true,
    },
  },
  data() {
    return {
      timeLeft: {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
      },
      interval: null,
    };
  },
  mounted() {
    this.startCountdown();
  },
  beforeDestroy() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  },
  computed: {
    /**
     * Returns days value split into individual digits with zero padding
     * @returns {Array} Array of digit strings
     */
    daysDigits() {
      const paddedValue = String(this.timeLeft.days).padStart(2, "0");
      return paddedValue.split("");
    },

    /**
     * Returns hours value split into individual digits with zero padding
     * @returns {Array} Array of digit strings
     */
    hoursDigits() {
      const paddedValue = String(this.timeLeft.hours).padStart(2, "0");
      return paddedValue.split("");
    },

    /**
     * Returns minutes value split into individual digits with zero padding
     * @returns {Array} Array of digit strings
     */
    minutesDigits() {
      const paddedValue = String(this.timeLeft.minutes).padStart(2, "0");
      return paddedValue.split("");
    },

    /**
     * Returns seconds value split into individual digits with zero padding
     * @returns {Array} Array of digit strings
     */
    secondsDigits() {
      const paddedValue = String(this.timeLeft.seconds).padStart(2, "0");
      return paddedValue.split("");
    },
  },
  methods: {
    /**
     * Starts the countdown timer
     */
    startCountdown() {
      this.updateTime();
      this.interval = setInterval(() => {
        this.updateTime();
      }, 1000);
    },

    /**
     * Updates the time left calculation
     */
    updateTime() {
      const now = new Date().getTime();
      const endTime = new Date(this.endTime).getTime();
      const distance = endTime - now;

      if (distance > 0) {
        this.timeLeft = {
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor(
            (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
          ),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000),
        };
      } else {
        this.timeLeft = { days: 0, hours: 0, minutes: 0, seconds: 0 };
        this.$emit("end");
        if (this.interval) {
          clearInterval(this.interval);
        }
      }
    },

    /**
     * Pads numbers with leading zeros
     * @param {number} num - Number to pad
     * @returns {string} Padded number string
     */
    zeroPad(num) {
      return zeroPad(num);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~/scss/include/_commonInclude.scss";

.content .app {
  .counter-stick__container {
    justify-content: space-between;
    align-items: center;
    width: 85%;
    gap: rem(12);
  }
  .counter-stick {
    width: rem(20);
    height: rem(35);
    background: linear-gradient(90deg, #f1e6a2 0%, #b08742 100%);
  }
  .counter-container {
    display: inline-flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    // padding: rem(20);
  }

  .counter-wrapper {
    border-radius: rem(16);
    padding: rem(8);
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .counter-border {
    border-radius: 18.644px;
    padding: rem(28) rem(32);
    padding-bottom: rem(20);
    border: 2.797px solid #f4eba7;

    @include media-breakpoint-down(md) {
      padding: rem(13) rem(15);
    }
  }

  .counter-content {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .time-section {
    display: flex;
    align-items: center;
    gap: rem(8);
    padding-bottom: rem(17);

    @include media-breakpoint-down(md) {
      gap: rem(8);
    }
  }

  .time-unit {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: rem(4);
    margin-bottom: rem(-17);
  }

  .time-value {
    font-size: rem(24);
    font-weight: bold;
    // padding: rem(8) rem(12);
    border-radius: rem(8);
    // min-width: rem(48);
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: rem(11);
    margin-bottom: rem(6);

    @include media-breakpoint-down(md) {
      font-size: rem(18);
      // padding: rem(6) rem(8);
      // min-width: rem(36);
      gap: rem(1);
    }
  }

  .digit {
    font-size: rem(52);
    line-height: 1.2;
    font-weight: inherit;
    min-width: rem(56);
    height: 100%;
    text-align: center;
    padding: rem(8) rem(12);

    @include media-breakpoint-down(md) {
      font-size: rem(28);
      padding: rem(4) 0;
      min-width: rem(30);
    }
  }

  .time-label {
    font-size: rem(12);
    height: rem(17);
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    @include media-breakpoint-down(md) {
      font-size: rem(10);
    }
  }

  .time-separator {
    font-size: rem(44);
    font-weight: bold;
    margin: 0;

    @include media-breakpoint-down(md) {
      font-size: rem(18);
      margin: 0;
    }
  }
}
</style>
